import { CartAPI } from '../../api/cart';
import { getProductById } from '../../api/preorder';
import { formatPrice } from '../../dynamicBundle/index.js';
import {
  buildUrlWithLocalization,
  getSessionStorage,
  getThemeSettings,
  isProductPage,
  setSessionStorage,
  setWindowLocation,
  splitGoogleFontStyle,
} from '../../utils';
import {
  KEY_VOLUME_BUNDLE,
  KEY_VOLUME_BUNDLE_DATA_SESSION,
  RECOMMENDATION_SELECTOR,
  VOLUME_BUNDLE_ACTION,
  VOLUME_BUNDLE_DEFAULT_SETTINGS,
  VOLUME_BUTTON_ADD_BUNDLE_TO_CART_SELECTOR,
  VOLUME_MENU_ITEM_SELECTOR,
} from '../constants';
import { closeVolumeBundleModal } from '../events/modal-events';

export const calculateDiscountedPrice = (totalOriginal, discount = {}, currencyRate = 1) => {
  if (!discount || !discount.discount_type) {
    const result = { rawDiscountedPrice: totalOriginal, rawOriginalPrice: totalOriginal };
    return result;
  }

  const { discount_type, discount_value } = discount;
  const rawDiscountedPrice =
    discount_type === 'percentage'
      ? totalOriginal - (totalOriginal * discount_value) / 100
      : Math.max(0, totalOriginal - discount_value * currencyRate);

  const result = { rawDiscountedPrice, rawOriginalPrice: totalOriginal };
  return result;
};

/**
 * @type {import('../index.d').volumeBundleData}
 */
export const volumeBundleData = async (
  context,
  bundle,
  { optionData, action, target, position }
) => {
  const [getVolumeBundleData, setVolumeBundleData] = context.useContextState(
    `${KEY_VOLUME_BUNDLE}-${bundle.widgetId}`,
    {}
  );

  const widgetId = bundle.widgetId || '';
  const widgetDesignSettings =
    context?.app?.recommendationWidgets?.['dynamic-bundle']?.[widgetId]?.widgetDesignSettings || {};
  const variantSelectionType = widgetDesignSettings.variantSelection || 'single';
  bundle.variantSelectionType = variantSelectionType;

  let product = bundle.products[0];

  if (isProductPage(context) && position === 'volumeBlock') {
    const fetchedProduct = await getProductById(context, window.boostSDData.product.id);
    const matchedProduct = bundle.products.find((p) => p.id === fetchedProduct.id);
    product = matchedProduct || fetchedProduct;
  }

  if (!product) return;

  product.variants = product.variants?.filter(filterProductAvailable) || [];

  // Ensure we have at least one valid variant before proceeding
  if (!Array.isArray(product.variants) || product.variants.length === 0) {
    console.warn('No valid variants found for product:', product.id);
    return;
  }

  let selectedVariant = product.variants.reduce((minVariant, currentVariant) => {
    // Additional safety check for variant structure
    if (!isValidVariant(currentVariant) || !isValidVariant(minVariant)) {
      return isValidVariant(minVariant) ? minVariant : currentVariant;
    }
    return parseFloat(currentVariant.price) < parseFloat(minVariant.price)
      ? currentVariant
      : minVariant;
  });

  if (action === VOLUME_BUNDLE_ACTION.changeVariant) {
    const foundVariant = product.variants.find(
      (variant) =>
        isValidVariant(variant) && variant.id === Number(optionData?.optionData?.variantId)
    );
    if (foundVariant) {
      selectedVariant = foundVariant;
    }
  }

  const { price: originPrice = 0, title = '', id = '', image = '' } = selectedVariant;

  product.selectedOption = {
    rawPrice: Number(originPrice),
    title,
    price: formatPrice(context, originPrice),
    originPrice: formatPrice(context, originPrice),
    minPrice: formatPrice(context, product.price_min),
    id,
    image: image ?? product.images_info?.[0]?.src,
  };

  const discounts = Array.isArray(bundle.discounts) ? bundle.discounts : [];
  const currencyRate = Number(window.Shopify?.currency?.rate || 1);
  const highlightOption = Number(widgetDesignSettings.bundleHighlightOption) || 0;
  bundle.bundleOptions = discounts.length
    ? discounts.map((discount, index) => {
        const itemsCount = discount.item;
        let totalOriginal = 0;

        // If multiple: add price according to multiple variants
        if (variantSelectionType === 'multiple') {
          for (let i = 0; i < itemsCount; i++) {
            // Ensure we have valid variants array and calculate safe index
            if (!Array.isArray(product.variants) || product.variants.length === 0) {
              console.warn('No variants available for multiple selection');
              break;
            }

            const variantIndex = i >= product.variants.length ? 0 : i;
            const variantCandidate = product.variants[variantIndex];

            // Strict validation to prevent object injection
            if (!isValidVariant(variantCandidate)) {
              console.warn('Invalid variant at index', variantIndex);
              continue;
            }

            totalOriginal += Number(variantCandidate.price);
          }
        } else {
          // If single: multiply the price of 1 variant by the number of items
          totalOriginal = Number(originPrice) * itemsCount;
        }

        // Calculate price after discount
        const { rawDiscountedPrice } = calculateDiscountedPrice(
          totalOriginal,
          discount,
          currencyRate
        );

        const discountValue = Number(discount.discount_value) * currencyRate;
        const showDiscount =
          discount.discount_type === 'percentage' ? discount.discount_value > 0 : discountValue > 0;
        const hasDiscount = showDiscount && rawDiscountedPrice < totalOriginal;

        return {
          id: `bundle-option-${index + 1}`,
          checked: highlightOption === index + 1,
          items: itemsCount,
          hasDiscount,
          discountLabel: hasDiscount
            ? `Buy ${discount.item} items and save ${
                discount.discount_type === 'percentage'
                  ? `${discount.discount_value}%`
                  : formatPrice(context, discount.discount_value * currencyRate)
              }`
            : '',
          rawOriginalPrice: totalOriginal,
          rawDiscountedPrice,
          originalPrice: formatPrice(context, totalOriginal),
          discountedPrice: formatPrice(context, rawDiscountedPrice),
          discountValueDisplay:
            hasDiscount && discount.discount_type === 'percentage'
              ? `${discount.discount_value}%`
              : hasDiscount
                ? formatPrice(context, discount.discount_value * currencyRate)
                : '',
          selectedDropdownOptions: {
            variantId: selectedVariant.id,
            discountedPrice: formatPrice(context, rawDiscountedPrice),
            originalPrice: formatPrice(context, totalOriginal),
            rawDiscountedPrice,
            title: selectedVariant.title,
            image: selectedVariant.image || product.images_info?.[0]?.src,
          },
        };
      })
    : [
        {
          id: 'bundle-option-0',
          checked: highlightOption === 0,
          items: 1,
          discountLabel: 'No Discount',
          rawOriginalPrice: originPrice,
          rawDiscountedPrice: originPrice,
          originalPrice: formatPrice(context, originPrice),
          discountedPrice: formatPrice(context, originPrice),
          discountValueDisplay: '0%',
          selectedDropdownOptions: {
            variantId: selectedVariant.id,
            discountedPrice: formatPrice(context, originPrice),
            originalPrice: formatPrice(context, originPrice),
            rawDiscountedPrice: originPrice,
            title: selectedVariant.title,
            image: selectedVariant.image || product.images_info?.[0]?.src,
          },
        },
      ];

  if (target instanceof NodeList) {
    target = target[0];
  }

  const selectedItems =
    Number(target?.closest(VOLUME_MENU_ITEM_SELECTOR)?.dataset?.items) ||
    bundle.bundleOptions[0].items;
  bundle.selectedVariant = bundle.bundleOptions.find((option) => option.items === selectedItems);
  bundle.matchedProduct = product;
  bundle.productMinPrice = formatPrice(context, product.price_min);

  const selectedBundleOption =
    bundle.bundleOptions.find((option) => option.checked) || bundle.bundleOptions[0];
  bundle.selectedOption = {
    ...selectedBundleOption,
    discountedPrice: formatPrice(context, selectedBundleOption.rawDiscountedPrice),
    originalPrice: formatPrice(context, selectedBundleOption.rawOriginalPrice),
  };

  setVolumeBundleData(bundle);

  const totalBundlePrice = bundle.selectedOption?.discountedPrice || 0;
  const newBundleDataSession = {
    ...getSessionStorage(KEY_VOLUME_BUNDLE_DATA_SESSION),
    [bundle.widgetId]: {
      widgetId: bundle.widgetId,
      bundle_type: bundle.bundle_type,
      recommendationType: bundle.recommendation_type,
      primary_algorithm: bundle.primary_algorithm || '',
      secondary_algorithm: bundle.secondary_algorithm || '',
      totalBundlePrice,
      bundleProductId: product.id,
    },
  };
  setSessionStorage(KEY_VOLUME_BUNDLE_DATA_SESSION, newBundleDataSession);

  return bundle;
};

/**
 * @type {import('../index.d').addVolumeBundleToCart}
 */
export const addVolumeBundleToCart = async (context, target) => {
  const widgetId = target?.closest(RECOMMENDATION_SELECTOR)?.dataset?.widgetId;
  const volumeBundleData = getVolumeBundleDataByWidgetId(context, widgetId);

  if (
    !volumeBundleData ||
    !Array.isArray(volumeBundleData.products) ||
    volumeBundleData.products.length === 0
  ) {
    console.error('No valid product found in the bundle.');
    return;
  }

  const data = [];
  const selectedOption = volumeBundleData.selectedOption;

  // setting variantSelection (single/multiple)
  const widgetDesignSettings =
    context?.app?.recommendationWidgets?.['dynamic-bundle']?.[volumeBundleData.widgetId]
      ?.widgetDesignSettings || {};
  const variantSelectionType = widgetDesignSettings.variantSelection || 'single';

  let bundleContainer = null;
  if (widgetId) {
    bundleContainer =
      document.querySelector(
        `${RECOMMENDATION_SELECTOR}[data-widget-id="${widgetId}"] .boost-sd__volume-bundle-menu`
      ) || document.querySelector(`[data-widget-id="${widgetId}"] .boost-sd__volume-bundle-menu`);
  }
  // fallback
  if (!bundleContainer) {
    bundleContainer = document.querySelector('.boost-sd__volume-bundle-menu');
  }

  // default product id from bundle data (server-side)
  const bundleProductId = volumeBundleData.matchedProduct?.id;

  if (variantSelectionType === 'multiple') {
    if (!bundleContainer) {
      console.warn(
        'Bundle container not found for multiple selection — fallback to single add behavior.'
      );
      data.push({
        id: selectedOption.selectedDropdownOptions.variantId,
        quantity: selectedOption.items,
        properties: {
          BoostBundle: '',
          _boostBundleId: volumeBundleData.widgetId,
          _boostBundleType: volumeBundleData.bundle_type,
          _boostItemPrice: selectedOption.selectedDropdownOptions.rawDiscountedPrice,
          _boostItemTitle: selectedOption.selectedDropdownOptions.title,
          _boostBundleProducts: bundleProductId,
        },
      });
    } else {
      // Get all rows (each row corresponds to 1 dropdown item)
      const activeItem =
        bundleContainer
          .querySelector('.boost-sd__volume-bundle-menu-radio--checked')
          ?.closest('.boost-sd__volume-bundle-menu-item') ||
        bundleContainer.querySelector('.boost-sd__volume-bundle-menu-item--highlight');

      // get all rows variant in that active group
      const variantRows =
        activeItem?.querySelectorAll('.boost-sd__volume-bundle-variant-row') || [];

      // fallback: use selectedOption.items first variant number
      if (!variantRows || variantRows.length === 0) {
        data.push({
          id: selectedOption.selectedDropdownOptions.variantId,
          quantity: selectedOption.items,
          properties: {
            BoostBundle: '',
            _boostBundleId: volumeBundleData.widgetId,
            _boostBundleType: volumeBundleData.bundle_type,
            _boostItemPrice: selectedOption.selectedDropdownOptions.rawDiscountedPrice,
            _boostItemTitle: selectedOption.selectedDropdownOptions.title,
            _boostBundleProducts: bundleProductId,
          },
        });
      } else {
        variantRows.forEach((row) =>
          handleVariantRow(row, data, selectedOption, volumeBundleData, bundleProductId)
        );
      }
    }
  } else {
    // SINGLE
    data.push({
      id: selectedOption.selectedDropdownOptions.variantId,
      quantity: selectedOption.items,
      properties: {
        BoostBundle: '',
        _boostBundleId: volumeBundleData.widgetId,
        _boostBundleType: volumeBundleData.bundle_type,
        _boostItemPrice: selectedOption.selectedDropdownOptions.rawDiscountedPrice,
        _boostItemTitle: selectedOption.selectedDropdownOptions.title,
        _boostBundleProducts: volumeBundleData.matchedProduct.id,
      },
    });
  }

  if (!data || data.length === 0) {
    console.error('No cart items prepared for bundle add to cart.');
    return;
  }

  // UI: add to cart
  const btnAddToCart = target?.closest(VOLUME_BUTTON_ADD_BUNDLE_TO_CART_SELECTOR);
  const ctaBtnText = btnAddToCart?.querySelector('span');
  const textAddBundleAddToCart = ctaBtnText?.innerText || 'Add bundle to cart';
  const element = ctaBtnText || target;
  element.textContent =
    context.app?.translation?.productItem?.atcAddingToCartBtnLabel || 'Adding...';
  btnAddToCart?.setAttribute('disabled', true);

  try {
    const res = await CartAPI.addMultiProductToCart(data);
    if (res.status === 200) {
      element.textContent =
        context.app?.translation?.productItem?.atcAddedToCartBtnLabel || 'Added!';
      const { enableCart, autoOpenAjaxCartOnAddToCart = true } =
        getThemeSettings(context)?.cart || {};
      const { noneCartOption = false } = window.boostWidgetIntegration || {};
      const shouldOpenCart = !noneCartOption && autoOpenAjaxCartOnAddToCart;
      if (enableCart) {
        const event = shouldOpenCart ? 'boost-sd-open-cart' : 'boost-sd-re-render-cart';
        window.dispatchEvent(new CustomEvent(event));
        closeVolumeBundleModal(context);
      } else {
        setWindowLocation(buildUrlWithLocalization('cart'));
      }
    } else {
      element.textContent =
        context.app?.translation?.productItem?.atcFailedToCartBtnLabel || 'Failed!';
      btnAddToCart?.removeAttribute('disabled');
    }
  } catch (err) {
    element.textContent =
      context.app?.translation?.productItem?.atcFailedToCartBtnLabel || 'Failed!';
    console.error('Add bundle to cart exception', err);
  }

  setTimeout(function updateTextAddBundleToCart() {
    element.textContent = textAddBundleAddToCart;
    btnAddToCart?.removeAttribute('disabled');
  }, 500);
};

/**
 * @type {import('../index.d').getVolumeBundleDataByWidgetId}
 */
export const getVolumeBundleDataByWidgetId = (context, widgetId) => {
  const [getVolumeBundleData] = context.useContextState(`${KEY_VOLUME_BUNDLE}-${widgetId}`, {});

  return {
    ...getVolumeBundleData(),
  };
};

/**
 * @type {import('../index.d').updateVolumeBundleData}
 */
export const updateVolumeBundleData = async (context, widgetId, data, action, target, position) => {
  const [getVolumeBundleData, setVolumeBundleData] = context.useContextState(
    `${KEY_VOLUME_BUNDLE}-${widgetId}`,
    {}
  );
  const oldDataVolumeBundle = getVolumeBundleData();

  if (!oldDataVolumeBundle) {
    console.error('Bundle data not found');
    return;
  }

  const newBundleData = await volumeBundleData(context, oldDataVolumeBundle, {
    optionData: data,
    action,
    target,
    position,
  });

  const checkedBundleOption = newBundleData.bundleOptions.find((option) => option.checked);

  const mergedBundleData = {
    ...oldDataVolumeBundle,
    matchedProduct: newBundleData.matchedProduct,
    selectedVariant: newBundleData.selectedVariant,
    selectedOption: checkedBundleOption
      ? {
          ...checkedBundleOption,
          discountedPrice: formatPrice(context, checkedBundleOption.rawDiscountedPrice),
          originalPrice: formatPrice(context, checkedBundleOption.rawOriginalPrice),
        }
      : null,
  };

  setVolumeBundleData(mergedBundleData);
  return mergedBundleData;
};

/**
 * @type {import('../index.d').filterProductAvailable}
 */
const filterProductAvailable = (product) => {
  return product?.available;
};

/**
 * Validates that a variant has the expected structure and required properties
 * @param {any} variant - The variant object to validate
 * @returns {boolean} - True if variant is valid, false otherwise
 */
const isValidVariant = (variant) => {
  return (
    variant &&
    typeof variant === 'object' &&
    !Array.isArray(variant) &&
    typeof variant.id !== 'undefined' &&
    typeof variant.price !== 'undefined' &&
    variant.price !== null &&
    !isNaN(Number(variant.price))
  );
};

/**
 * @type {import('../index.d').getVolumeBundleSettingsByWidgetId}
 */
export const getVolumeBundleSettingsByWidgetId = (context, widgetId) => {
  let widgetDesignSettings =
    context?.app?.recommendationWidgets?.['dynamic-bundle']?.[widgetId]?.widgetDesignSettings || {};

  const dynamicDefaultSettings =
    context?.app?.recommendationWidgets?.dynamicBundleDefaultSettings ||
    VOLUME_BUNDLE_DEFAULT_SETTINGS;

  widgetDesignSettings = {
    ...dynamicDefaultSettings,
    ...widgetDesignSettings,
  };

  const { titleFontStyle } = widgetDesignSettings;
  const { fontWeight, fontStyle } = splitGoogleFontStyle(titleFontStyle);

  return {
    widgetDesignSettings: {
      ...widgetDesignSettings,
      titleFontStyle: fontStyle,
      titleFontWeight: fontWeight,
    },
  };
};

const getValidWidgetId = (bundleContainer) => {
  const widgetRoot =
    bundleContainer?.closest(RECOMMENDATION_SELECTOR) ||
    bundleContainer?.closest('[data-widget-id]');
  return widgetRoot?.dataset?.widgetId || null;
};

/**
 * @type {import('../index.d').updateMultipleBundlePrice}
 */
export const updateMultipleBundlePrice = (context, bundleContainer, specificItem) => {
  const widgetId = getValidWidgetId(bundleContainer);
  if (!bundleContainer || !widgetId) return;

  const bundleState = getVolumeBundleDataByWidgetId(context, widgetId);
  const product = bundleState?.matchedProduct;
  const discounts = Array.isArray(bundleState?.discounts) ? bundleState.discounts : [];
  if (!product?.variants?.length) return;

  const activeItem = getActiveItem(bundleContainer, specificItem);
  if (!activeItem) return;

  const { totalOriginal, discounted, selectedVariants } = calculateTotalAndDiscount(
    activeItem,
    product,
    discounts
  );

  updateUIAndSaveState(context, bundleContainer, activeItem, {
    totalOriginal,
    discounted,
    selectedVariants,
  });
};

/** -----------------------------
 * Helpers
 * ----------------------------- */

/**
 * Handles a single variant row and pushes its data into `data` array.
 * Reduced cyclomatic complexity by splitting guard clauses and helper extraction.
 */
const handleVariantRow = (row, data, selectedOption, volumeBundleData, bundleProductId) => {
  if (!row) return;

  const selectedItem = getSelectedItem(row);
  if (!selectedItem) return;

  const itemData = extractVariantItemData(selectedItem, volumeBundleData, selectedOption);
  if (!itemData) return;

  const { variantId, variantPrice, variantTitle } = itemData;
  const rowProductId =
    row.closest('.boost-sd__volume-bundle-menu')?.dataset?.productId ||
    bundleProductId ||
    volumeBundleData?.matchedProduct?.id;

  data.push({
    id: variantId,
    quantity: 1,
    properties: buildBundleProperties(
      volumeBundleData,
      selectedOption,
      variantPrice,
      variantTitle,
      rowProductId
    ),
  });
};

/** Helper: Extracts variant info safely */
const extractVariantItemData = (selectedItem, volumeBundleData, selectedOption) => {
  const { variantId: rawId, variantPrice, variantTitle } = selectedItem.dataset || {};
  if (!rawId) return null;

  const product = volumeBundleData?.matchedProduct;
  const variant = product?.variants?.find(
    (v) => isValidVariant(v) && Number(v.id) === Number(rawId)
  );
  if (!variant || variant.available === false) {
    console.warn('Variant unavailable — skipping row.', selectedItem);
    return null;
  }

  return {
    variantId: Number(rawId),
    variantPrice: variantPrice
      ? Number(variantPrice)
      : selectedOption.selectedDropdownOptions.rawDiscountedPrice,
    variantTitle: variantTitle || selectedItem.textContent.trim(),
  };
};

/** Helper: builds bundle item properties */
const buildBundleProperties = (
  volumeBundleData,
  selectedOption,
  variantPrice,
  variantTitle,
  rowProductId
) => {
  return {
    BoostBundle: '',
    _boostBundleId: volumeBundleData.widgetId,
    _boostBundleType: volumeBundleData.bundle_type,
    _boostItemPrice: variantPrice,
    _boostItemTitle: variantTitle || selectedOption.selectedDropdownOptions.title,
    _boostBundleProducts: rowProductId,
  };
};

/**
 * Helper: find selected variant item in a row
 */
const getSelectedItem = (row) => {
  return (
    row.querySelector('.boost-sd__dropdown-option--selected') ||
    row.querySelector('.boost-sd__dropdown-volume-item.boost-sd__dropdown-option--selected') ||
    row.querySelector('.boost-sd__dropdown-volume-item[data-variant-id]')
  );
};

const getActiveItem = (bundleContainer, specificItem) => {
  return (
    specificItem ||
    bundleContainer
      .querySelector('.boost-sd__volume-bundle-menu-radio--checked')
      ?.closest('.boost-sd__volume-bundle-menu-item') ||
    bundleContainer.querySelector('.boost-sd__volume-bundle-menu-item--highlight')
  );
};

/**
 * calculate total and discount price
 */
const calculateTotalAndDiscount = (activeItem, product, discounts) => {
  const itemsCount = Number(activeItem.dataset.items || 0);
  const discount = discounts.find((d) => Number(d.item) === itemsCount);
  const variantRows = activeItem.querySelectorAll('.boost-sd__volume-bundle-variant-row');
  if (!variantRows.length) return { totalOriginal: 0, discounted: 0, selectedVariants: [] };

  let totalOriginal = 0;
  const selectedVariants = [];

  variantRows.forEach((row) => {
    const selected = row.querySelector('.boost-sd__dropdown-option--selected');
    const id = selected?.dataset.variantId || row.dataset.baseVariantId;
    const variant = product.variants.find((v) => isValidVariant(v) && Number(v.id) === Number(id));
    if (!variant) return;
    selectedVariants.push(id);
    totalOriginal += Number(variant.price);
  });

  const { rawDiscountedPrice: discounted } = calculateDiscountedPrice(
    totalOriginal,
    discount,
    Number(window.Shopify?.currency?.rate || 1)
  );

  return { totalOriginal, discounted, selectedVariants };
};

/**
 * Update UI and save selected variants
 */
const updateUIAndSaveState = (
  context,
  bundleContainer,
  activeItem,
  { totalOriginal, discounted, selectedVariants }
) => {
  const discountedEl = activeItem.querySelector('.boost-sd__volume-bundle-menu-discounted-price');
  const originalEl = activeItem.querySelector('.boost-sd__volume-bundle-menu-original-price');

  if (discountedEl) discountedEl.textContent = formatPrice(context, discounted);
  if (originalEl) originalEl.textContent = formatPrice(context, totalOriginal);

  bundleContainer.dataset.selectedVariants = JSON.stringify(selectedVariants);
};
